import Link from 'next/link';

import MenuToggle from '@/components/MenuToggle';
import { Search } from '@/components/docs/search';
import LogoSVG from '@/svg/logo.svg';

interface HeaderProps {
  menuOpen: boolean;
  toggleNav: () => void;
}

export default function Header({ menuOpen, toggleNav }: HeaderProps) {
  return (
    <header className="sticky top-0 z-40 w-full bg-white h-16 md:h-20 backdrop-filter backdrop-blur border-b border-athens-gray text-primary-800">
      <div className="h-full max-w-7xl mx-auto flex items-center">
        <div className="w-full md:flex-shrink-0 md:w-64 px-6 md:px-8 flex items-center h-full">
          <Link href="/docs" prefetch={false}>
            <a className="inline-block focus:outline-none focus:text-haiti">
              <LogoSVG className="h-7 xl:h-8 fill-current" />
            </a>
          </Link>
        </div>

        <div className="flex-1 pr-6 md:px-8">
          <div className="flex items-center justify-between space-x-6 md:space-x-8">
            <div className="w-full">
              <Search />
            </div>
            <div className="hidden md:flex md:items-center md:space-x-3">
              <a
                href="https://github.com/graphcms"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:block text-gray-500 hover:text-gray-600 focus:text-haiti focus:outline-none transition-colors duration-200"
              >
                <svg
                  className="w-6 h-6 fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path d="M12 2C6.475 2 2 6.475 2 12a9.994 9.994 0 0 0 6.838 9.488c.5.087.687-.213.687-.476 0-.237-.013-1.024-.013-1.862-2.512.463-3.162-.612-3.362-1.175-.113-.288-.6-1.175-1.025-1.413-.35-.187-.85-.65-.013-.662.788-.013 1.35.725 1.538 1.025.9 1.512 2.338 1.087 2.912.825.088-.65.35-1.087.638-1.337-2.225-.25-4.55-1.113-4.55-4.938 0-1.088.387-1.987 1.025-2.688-.1-.25-.45-1.275.1-2.65 0 0 .837-.262 2.75 1.026a9.28 9.28 0 0 1 2.5-.338c.85 0 1.7.112 2.5.337 1.912-1.3 2.75-1.024 2.75-1.024.55 1.375.2 2.4.1 2.65.637.7 1.025 1.587 1.025 2.687 0 3.838-2.337 4.688-4.562 4.938.362.312.675.912.675 1.85 0 1.337-.013 2.412-.013 2.75 0 .262.188.574.688.474A10.016 10.016 0 0 0 22 12c0-5.525-4.475-10-10-10z" />
                </svg>
                <span className="sr-only">GitHub</span>
              </a>
              <a
                href="https://slack.graphcms.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:block text-gray-500 hover:text-gray-600 focus:text-haiti focus:outline-none transition-colors duration-200"
              >
                <svg
                  className="w-6 h-6 fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path d="M6.527 14.514A1.973 1.973 0 0 1 4.56 16.48a1.973 1.973 0 0 1-1.967-1.967c0-1.083.884-1.968 1.967-1.968h1.968v1.968zm.992 0c0-1.083.884-1.968 1.967-1.968 1.083 0 1.968.885 1.968 1.968v4.927a1.973 1.973 0 0 1-1.968 1.967 1.973 1.973 0 0 1-1.967-1.967v-4.927zm1.967-7.987A1.973 1.973 0 0 1 7.52 4.56c0-1.083.884-1.967 1.967-1.967 1.083 0 1.968.884 1.968 1.967v1.968H9.486zm0 .992c1.083 0 1.968.884 1.968 1.967a1.973 1.973 0 0 1-1.968 1.968H4.56a1.973 1.973 0 0 1-1.967-1.968c0-1.083.884-1.967 1.967-1.967h4.927zm7.987 1.967c0-1.083.885-1.967 1.968-1.967s1.967.884 1.967 1.967a1.973 1.973 0 0 1-1.967 1.968h-1.968V9.486zm-.992 0a1.973 1.973 0 0 1-1.967 1.968 1.973 1.973 0 0 1-1.968-1.968V4.56c0-1.083.885-1.967 1.968-1.967s1.967.884 1.967 1.967v4.927zm-1.967 7.987c1.083 0 1.967.885 1.967 1.968a1.973 1.973 0 0 1-1.967 1.967 1.973 1.973 0 0 1-1.968-1.967v-1.968h1.968zm0-.992a1.973 1.973 0 0 1-1.968-1.967c0-1.083.885-1.968 1.968-1.968h4.927c1.083 0 1.967.885 1.967 1.968a1.973 1.973 0 0 1-1.967 1.967h-4.927z" />
                </svg>
                <span className="sr-only">GrapCMS Slack Community</span>
              </a>
            </div>

            <a
              href="https://app.graphcms.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex md:items-center md:flex-shrink-0 text-gray-500 hover:text-gray-600 focus:text-haiti focus:outline-none transition-colors duration-200"
            >
              <span className="hidden md:block mr-3">Launch GraphCMS</span>
              <svg
                className="w-6 h-6 fill-current"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z" />
              </svg>
            </a>

            <MenuToggle
              className="md:hidden text-current"
              open={menuOpen}
              onClick={toggleNav}
            />
          </div>
        </div>
      </div>
    </header>
  );
}
